/**
 * Shared member-administration Zod contract.
 *
 * Source of truth for the post-onboarding admin surface (Innstillinger →
 * Administrasjon → Familiemedlemmer). apps/web Zod-validates the form before
 * POST/PATCH; apps/api Zod-validates every payload before touching the DB.
 *
 * Relationship to onboarding (./onboarding.ts):
 *   - `AdminCreateMember` is the SAME shape as `OnboardingMember` — a member
 *     added by an admin after first-run carries exactly the same fields and
 *     the same safety gate. We reuse the schema rather than fork it so the
 *     two paths can never drift.
 *   - `AdminUpdateMember` differs only in that the PIN is OPTIONAL: editing a
 *     member need not reset their PIN. When `pin` is present it must still be
 *     4–8 digits and match `pinConfirm`.
 *
 * Safety-critical note (PROJECT.md): every write path keeps
 * `allergies.confirmed === z.literal(true)`. Editing a member's allergies
 * forces the admin to re-confirm — the merge-blocking gate applies to edits
 * exactly as it does to creates (severe nut + egg allergies in the seed
 * household).
 */
import { z } from 'zod';
import {
  COLOR_SLOTS,
  ALLERGY_CHIPS,
  DIETARY_CHIPS,
  OnboardingMember,
} from './onboarding';
import { Password, Username } from './auth';

// ─── AdminCreateMember — identical contract to the onboarding member step ───
export const AdminCreateMember = OnboardingMember;
export type AdminCreateMemberInput = z.infer<typeof AdminCreateMember>;

// ─── AdminUpdateMember — full replace of editable fields; PIN optional ──────
// PATCH semantics are full-replacement of the editable fields (the form always
// submits every field, pre-filled from current values). The PIN is the one
// exception: omit it to leave the existing PIN untouched.
export const AdminUpdateMember = z
  .object({
    name: z.string().trim().min(1).max(60),
    shortName: z.string().trim().max(40).optional().nullable(),
    role: z.enum(['admin', 'teen', 'child']),
    birthDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD')
      .refine((s) => new Date(s) <= new Date(), 'Birth date cannot be in the future'),
    colorSlot: z.enum(COLOR_SLOTS),
    allergies: z.object({
      chips: z.array(z.enum(ALLERGY_CHIPS)).default([]),
      custom: z.array(z.string().trim().min(1).max(40)).default([]),
      // Merge-blocking safety gate — applies to edits too (D-02 / PROJECT.md).
      confirmed: z.literal(true),
    }),
    dietary: z.object({
      chips: z.array(z.enum(DIETARY_CHIPS)).default([]),
      custom: z.array(z.string().trim().min(1).max(40)).default([]),
    }),
    notes: z.string().trim().max(400).optional().nullable(),
    // Utelatt → brukernavnet beholdes (#140).
    username: Username.optional(),
    // Optional PIN reset. Both present-and-matching, or both absent.
    pin: z.string().regex(/^\d{4,8}$/, 'PIN must be 4-8 digits').optional(),
    pinConfirm: z.string().optional(),
  })
  .refine((v) => (v.pin === undefined ? true : v.pin === v.pinConfirm), {
    message: 'PIN confirmation must match',
    path: ['pinConfirm'],
  });
export type AdminUpdateMemberInput = z.infer<typeof AdminUpdateMember>;

// ─── Member invite (member-phone enrolment) ────────────────────────────────
// #139: invitasjonen setter passord (innlogging) og eventuelt PIN
// (hurtigbytte på kjøkkenskjermen). Minst én av dem må være med.
export const MemberInviteAccept = z
  .object({
    password: Password.optional(),
    passwordConfirm: z.string().optional(),
    pin: z.string().regex(/^\d{4,8}$/, 'PIN must be 4-8 digits').optional(),
    pinConfirm: z.string().optional(),
  })
  .refine((v) => v.password !== undefined || v.pin !== undefined, {
    message: 'password_or_pin_required',
    path: ['password'],
  })
  .refine((v) => v.password === v.passwordConfirm, {
    message: 'password_mismatch',
    path: ['passwordConfirm'],
  })
  .refine((v) => v.pin === v.pinConfirm, {
    message: 'PIN confirmation must match',
    path: ['pinConfirm'],
  });
export type MemberInviteAcceptInput = z.infer<typeof MemberInviteAccept>;

export interface MemberInviteCreateResult {
  memberId: string;
  memberName: string;
  role: 'admin' | 'teen' | 'child';
  inviteUrl: string;
  expiresAt: string;
}

export interface MemberInvitePreview {
  memberId: string;
  /** Familiekoden (D5), så lenken til innloggingen kan fylle den inn (FF-11). */
  familyCode: string;
  memberName: string;
  username: string | null;
  role: 'admin' | 'teen' | 'child';
  expiresAt: string;
  /**
   * #140: medlemmet har allerede valgt et eget passord / har PIN. Da svarer
   * accept 400 `already_enrolled` for det feltet — et nytt passord går via
   * lenken for nytt passord, ikke via en invitasjon. En admin som bare har
   * arvet husstandspassordet, har ikke eget passord.
   */
  hasPassword: boolean;
  hasPin: boolean;
}

// ─── MemberDietaryRow — GET /api/members/dietary (#148) ───────────────────
// Allergibrikken og -arket på handlelista: per medlem i egen familie, bare
// kortnavn, farge og allergi-/kostholdsbrikker. Aldri etternavn, notater,
// fødselsdato eller hasher. Brikkene er `string[]`, ikke leksikon-enumen:
// lagrede id-er utenfor dagens leksikon sendes med og vises som de er.
export interface MemberDietaryRow {
  id: string;
  short_name: string;
  color_token: string;
  allergies: { chips: string[]; custom: string[] };
  dietary: { chips: string[]; custom: string[] };
}

// ─── MemberAdminRow — the full row the admin list/edit form reads ───────────
// Returned by GET /api/members (admin-only). Sensitive columns (pin_hash,
// password_hash) are NEVER part of this shape — see members.routes.ts.
export interface MemberAdminDietary {
  allergies: { chips: AllergyChip[]; custom: string[]; confirmed: boolean };
  dietary: { chips: DietaryChip[]; custom: string[] };
  notes: string | null;
}

export interface MemberAdminRow {
  id: string;
  name: string;
  short_name: string;
  role: 'admin' | 'teen' | 'child';
  birth_date: string;
  color_token: string;
  username: string | null;
  has_password: boolean;
  has_pin: boolean;
  dietary: MemberAdminDietary;
  archived_at: string | null;
  created_at: string;
}

type AllergyChip = (typeof ALLERGY_CHIPS)[number];
type DietaryChip = (typeof DIETARY_CHIPS)[number];
